import type { FetchState } from "../hooks/useLazyAboEnrichment";

/**
 * Calculates the batch of AffAbo IDs to fetch based on visible rows and fetch states.
 *
 * This pure function determines which IDs need to be fetched by:
 * 1. Finding the first unfetched visible row
 * 2. Starting from that index, collecting up to batchSize unfetched IDs
 *
 * @param visibleAffAboIds - Array of AffAbo IDs currently visible in the viewport
 * @param allAffAboIds - Array of all AffAbo IDs in the table (in order)
 * @param getFetchState - Function to get the fetch state for a given AffAbo ID
 * @param batchSize - Maximum number of IDs to include in the batch
 * @returns Array of AffAbo IDs to fetch (max length: batchSize)
 */
export function calculateBatchToFetch(
  visibleAffAboIds: string[],
  allAffAboIds: string[],
  getFetchState: (id: string) => FetchState,
  batchSize: number,
): string[] {
  // Find first unfetched visible row
  const firstUnfetchedIndex = allAffAboIds.findIndex(
    (id) => visibleAffAboIds.includes(id) && getFetchState(id) === "unfetched",
  );

  if (firstUnfetchedIndex === -1) return [];

  // Get next batchSize unfetched rows starting from that index
  const batch: string[] = [];
  for (let i = firstUnfetchedIndex; i < allAffAboIds.length && batch.length < batchSize; i++) {
    const id = allAffAboIds[i];
    if (getFetchState(id) === "unfetched") {
      batch.push(id);
    }
  }

  return batch;
}
